/**
 * Guide Route
 * Complete guide for creating professional invoices
 */

import { createFileRoute } from '@tanstack/react-router';
import { SEOHead } from '@/components/seo/SEOHead';
import { StructuredData, generateBreadcrumbSchema, generateArticleSchema } from '@/components/seo/StructuredData';
import { PageLayout } from '@/components/layout/PageLayout';

function GuidePage() {
  // Article Schema
  const articleData = generateArticleSchema({
    headline: "Complete Guide to Creating Professional Invoices: Best Practices, Examples, and Tips",
    description: "Learn how to create professional invoices that get paid faster. Includes best practices, legal requirements, examples for different industries, and expert tips.",
    datePublished: "2026-02-05",
    dateModified: "2026-02-05",
    authorName: "Smart Invoice Generator Team",
    publisherName: "Smart Invoice Generator"
  });

  // Breadcrumb Schema
  const breadcrumbData = generateBreadcrumbSchema([
    { name: "Home", url: "https://invoicegenerated.com/" },
    { name: "Invoice Guide", url: "https://invoicegenerated.com/guide" }
  ]);

  return (
    <>
      <SEOHead
        title="Complete Invoice Guide - Smart Invoice Generator | How to Create Professional Invoices"
        description="Learn how to create professional invoices that get paid faster. Comprehensive guide covering invoice best practices, legal requirements, industry examples, payment terms, and expert tips for freelancers and businesses."
        keywords="invoice guide, how to create invoice, professional invoice template, invoice best practices, payment terms, invoice examples, freelance invoice, business invoicing tips"
        canonical="https://invoicegenerated.com/guide"
      />
      <StructuredData data={articleData} />
      <StructuredData data={breadcrumbData} />

      <PageLayout>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 sm:p-12">
            {/* Page Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Complete Guide to Creating Professional Invoices
            </h1>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
              Professional invoices are powerful tools that reflect your business credibility and help you get paid faster. This comprehensive guide covers everything you need to know.
            </p>

            {/* Table of Contents */}
            <section className="mb-10 bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Table of Contents
              </h2>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li><a href="#what-is-invoice" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">What is an Invoice?</a></li>
                <li><a href="#essential-elements" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">Essential Elements Every Invoice Must Include</a></li>
                <li><a href="#payment-terms" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">Understanding Payment Terms</a></li>
                <li><a href="#industry-examples" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">Invoice Examples by Industry</a></li>
                <li><a href="#best-practices" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">Invoice Best Practices for Faster Payment</a></li>
                <li><a href="#legal-considerations" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">Legal & Tax Considerations</a></li>
              </ul>
            </section>

            {/* What is an Invoice */}
            <section id="what-is-invoice" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                What is an Invoice?
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                An invoice is a formal document sent by a business to a client requesting payment for goods delivered or services provided. It serves as a legal record of the transaction, establishes payment terms, and helps both parties maintain accurate financial records.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Unlike a receipt (which proves payment was made), an invoice is a request for payment. Well-crafted invoices clearly communicate what was provided, what is owed, and when payment is due—reducing misunderstandings and payment delays.
              </p>
            </section>

            {/* Essential Elements */}
            <section id="essential-elements" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Essential Elements Every Invoice Must Include
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                A professional invoice should include the following key elements to ensure clarity, legal compliance, and prompt payment:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">1. Unique Invoice Number</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">A unique identifier for tracking and bookkeeping. Use sequential numbers (001, 002, 003) or a system like INV-2024-001.</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">2. Date Issued & Due Date</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Clearly state when the invoice was issued and when payment is expected to avoid confusion and late payments.</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">3. Business Information</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Your business name, address, contact details, and logo for professional presentation and legal identification.</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">4. Client Details</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Client's name, company name, address, and contact information for proper record-keeping and payment processing.</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">5. Line Items</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Detailed description of products/services with quantities, unit prices, and total amounts for transparency.</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">6. Total Amount Due</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Clear subtotal, taxes, discounts, shipping, and final total prominently displayed to avoid payment confusion.</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">7. Payment Terms</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Specify payment due date, accepted payment methods, and any late payment fees or early payment discounts.</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">8. Tax Information</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Include tax ID, VAT number, or GST registration if applicable in your jurisdiction for tax compliance.</p>
                </div>
              </div>
            </section>

            {/* Payment Terms */}
            <section id="payment-terms" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Understanding Payment Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Payment terms specify when you expect to be paid. Choosing the right terms depends on your industry, client relationship, and cash flow needs:
              </p>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Due on Receipt / Net 0</h3>
                  <p className="text-gray-700 dark:text-gray-300">Payment expected immediately. Common for retail, one-time services, or new clients with no established credit relationship.</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Net 15</h3>
                  <p className="text-gray-700 dark:text-gray-300">Payment due within 15 days. A good middle ground for smaller projects and ongoing client relationships.</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Net 30</h3>
                  <p className="text-gray-700 dark:text-gray-300">Payment due within 30 days. Standard for B2B services, consulting work, and established business relationships.</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Net 60 / Net 90</h3>
                  <p className="text-gray-700 dark:text-gray-300">Longer terms for large projects, government clients, or well-established business relationships with proven payment history.</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Early Payment Discounts</h3>
                  <p className="text-gray-700 dark:text-gray-300">Offer discounts like "2/10 Net 30" (2% discount if paid within 10 days, otherwise full amount due in 30 days) to encourage faster payment.</p>
                </div>
              </div>
            </section>

            {/* Industry Examples */}
            <section id="industry-examples" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Invoice Examples by Industry
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Different industries have unique invoicing needs. Here are tailored examples for common professions:
              </p>

              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Freelance Consultant Invoice</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">For consulting, coaching, or professional services:</p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
                    <li><strong>Structure:</strong> Hourly rate × hours worked per session/meeting</li>
                    <li><strong>Line Items:</strong> "Consulting Services - [Project Name] - [Date] - [Hours] hours @ [Rate]/hour"</li>
                    <li><strong>Include:</strong> Preparation time, post-work deliverables, and client meeting time</li>
                    <li><strong>Specify:</strong> Project scope to prevent scope creep and additional charges</li>
                    <li><strong>Terms:</strong> Add late payment fees or early payment discounts as appropriate</li>
                  </ul>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Creative Professional Invoice</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">For designers, photographers, writers, and artists:</p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
                    <li><strong>Structure:</strong> Project-based pricing or per-piece rates</li>
                    <li><strong>Line Items:</strong> "[Service Type] - [Project Name] - [Deliverable Description]"</li>
                    <li><strong>Specify:</strong> Number of revisions included in the base price</li>
                    <li><strong>Add-ons:</strong> Charge extra for rush orders, additional revisions, or licensing fees</li>
                    <li><strong>Include:</strong> Usage rights or licensing terms if applicable (e.g., "Exclusive rights for 1 year")</li>
                  </ul>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Service Business Invoice</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">For contractors, repair services, maintenance providers:</p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
                    <li><strong>Structure:</strong> Break down labor hours and materials separately</li>
                    <li><strong>Line Items:</strong> "Labor - [Technician Name] - [Hours] hours @ [Rate]/hour" and "Materials - [Item] - [Quantity] × [Price]"</li>
                    <li><strong>Include:</strong> Service dates, technician names, and location</li>
                    <li><strong>Add-ons:</strong> Travel fees, emergency service charges, or disposal fees</li>
                    <li><strong>Specify:</strong> Warranty information on work performed and parts</li>
                  </ul>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Retainer-Based Invoice</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">For ongoing services, marketing agencies, legal services:</p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
                    <li><strong>Structure:</strong> Monthly retainer fee + additional services as needed</li>
                    <li><strong>Line Items:</strong> "Retainer - [Month] - Services included" and itemized additional services</li>
                    <li><strong>Include:</strong> Summary of work performed during the billing period</li>
                    <li><strong>Track:</strong> Hours worked vs. retainer hours to show value</li>
                    <li><strong>Terms:</strong> Auto-renewal clauses and notice periods for cancellations</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Best Practices */}
            <section id="best-practices" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Invoice Best Practices for Faster Payment
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Following these best practices will help you get paid faster and maintain positive client relationships:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-lg">✓ Do</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li>• Send invoices immediately after service completion</li>
                    <li>• Use clear, professional formatting and consistent branding</li>
                    <li>• Include multiple payment methods (bank transfer, credit card, PayPal)</li>
                    <li>• Add late payment penalties if allowed by law in your jurisdiction</li>
                    <li>• Offer early payment discounts (e.g., 2% off for payment within 10 days)</li>
                    <li>• Follow up politely 3-5 days before the due date</li>
                    <li>• Use sequential invoice numbering for easy tracking</li>
                    <li>• Include your tax ID or business registration number</li>
                    <li>• Proofread carefully for errors or typos before sending</li>
                    <li>• Save copies of all sent invoices for your records</li>
                  </ul>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-lg">✗ Don't</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li>• Wait until month-end to send invoices (delays payment)</li>
                    <li>• Use vague or confusing line item descriptions</li>
                    <li>• Forget to proofread for errors or typos</li>
                    <li>• Make payment terms hard to find or understand</li>
                    <li>• Skip including your complete contact information</li>
                    <li>• Neglect to follow up on overdue payments</li>
                    <li>• Use inconsistent invoice numbering systems</li>
                    <li>• Leave out important details like dates or purchase order numbers</li>
                    <li>• Send handwritten or unprofessional invoices</li>
                    <li>• Ignore client invoicing preferences or requirements</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-lg">Pro Tips for Faster Payment</h3>
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li>• <strong>Set up automated reminders:</strong> Schedule polite reminder emails for 3 days before and 3 days after the due date</li>
                  <li>• <strong>Use online invoicing tools:</strong> Allow clients to pay instantly with a "Pay Now" button</li>
                  <li>• <strong>Get it in writing:</strong> Confirm payment terms and invoicing requirements before starting work</li>
                  <li>• <strong>Build relationships:</strong> A quick check-in call or email can prevent payment delays</li>
                  <li>• <strong>Offer flexibility:</strong> Payment plans for large invoices can improve cash flow for both parties</li>
                </ul>
              </div>
            </section>

            {/* Legal & Tax Considerations */}
            <section id="legal-considerations" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Legal & Tax Considerations
              </h2>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-6 rounded-r mb-6">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-3">Important Disclaimer</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Smart Invoice Generator helps you create professional invoices, but we cannot guarantee compliance with all legal requirements in your jurisdiction. Invoice requirements vary significantly by country, state, and industry. <strong>Consult with a qualified accountant or tax professional</strong> for advice specific to your location and business.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Key Considerations:</h3>
                <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span><strong>Tax ID Numbers:</strong> Include your VAT, GST, EIN, or tax identification number as required by local tax laws. In the EU, VAT numbers are mandatory for B2B transactions.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span><strong>Business Registration:</strong> Include your business registration number, legal structure (LLC, Sole Proprietorship, etc.), and registered business address where required.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span><strong>Tax Compliance:</strong> Understand when to charge sales tax, VAT, GST, or other indirect taxes. Tax rules vary by product/service type and location.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span><strong>Currency Regulations:</strong> For international clients, specify the currency clearly and understand any foreign exchange reporting requirements.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span><strong>Record Keeping:</strong> Retain copies of all invoices (both sent and paid) for the period required by tax authorities (typically 5-7 years).</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span><strong>Late Payment Laws:</strong> Some jurisdictions regulate late payment fees and interest rates. Check local laws before adding penalty clauses.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span><strong>Consumer Protection:</strong> B2C invoices may have different requirements than B2B invoices. Understand your obligations.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Quick Reference Checklist */}
            <section className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Quick Invoice Checklist
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Before sending your invoice, run through this quick checklist to ensure everything is complete and professional:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Unique invoice number included</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Issue date and due date clearly stated</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Your business name, address, and contact information</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Client's complete billing information</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Detailed line items with descriptions and quantities</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Subtotal, taxes, discounts clearly shown</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Final total amount due prominently displayed</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Payment terms and accepted payment methods listed</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Tax ID or business registration number (if applicable)</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Company logo and branding applied</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Proofread for spelling, grammar, and calculation errors</span>
                  </li>
                  <li className="flex items-center">
                    <input type="checkbox" className="mr-3 h-4 w-4 text-blue-600 rounded" readOnly />
                    <span>Saved a copy for your records</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-blue-50 dark:bg-blue-900/20 p-8 rounded-lg text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Ready to Create Your Professional Invoice?
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg">
                Join thousands of freelancers and businesses using Smart Invoice Generator. Create professional, customizable invoices in seconds—completely free.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm mb-6">
                <span className="flex items-center text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  No sign-up required
                </span>
                <span className="flex items-center text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Unlimited invoices
                </span>
                <span className="flex items-center text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  150+ currencies supported
                </span>
                <span className="flex items-center text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  PDF & PNG export
                </span>
              </div>
              <a
                href="/"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
              >
                Create Free Invoice Now
              </a>
            </section>
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export const Route = createFileRoute('/guide')({
  component: GuidePage,
});
